import { Link } from 'react-router-dom';

/**
 * Small "Back" link at the very bottom of each lesson. Points to the
 * previous lesson slug. Rules:
 *   - X.Y with Y > 1  -> X.(Y-1)
 *   - X.1  with X > 1 -> (X-1).5
 *   - 1.1             -> Back to dashboard
 *
 * We render nothing at all on 1.1's bottom nav because the "Up next" card
 * already provides forward motion, and the dashboard is one click away
 * from the top nav. The rule above still returns dashboard so the caller
 * can decide.
 */
export default function PrevLessonLink({ currentSlug }: { currentSlug: string }) {
  const prev = getPrevLessonSlug(currentSlug);

  if (!prev) {
    return (
      <div className="mx-auto mt-8 max-w-3xl px-6 pb-16">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-muted transition hover:text-amber"
        >
          <span aria-hidden>←</span>
          <span>Back to dashboard</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-8 max-w-3xl px-6 pb-16">
      <Link
        to={`/preview/lesson/${prev}`}
        className="inline-flex items-center gap-1.5 text-sm text-muted transition hover:text-amber"
      >
        <span aria-hidden>←</span>
        <span>Back to Lesson {prev}</span>
      </Link>
    </div>
  );
}

/**
 * Compute previous lesson slug. Lessons run 1.1 ... 1.5, 2.1 ... 2.5, ...,
 * 8.1 ... 8.5. Returns null when at 1.1.
 */
export function getPrevLessonSlug(slug: string): string | null {
  const m = slug.match(/^(\d+)\.(\d+)$/);
  if (!m) return null;
  const week = Number(m[1]);
  const lesson = Number(m[2]);
  if (lesson > 1) return `${week}.${lesson - 1}`;
  if (week > 1) return `${week - 1}.5`;
  return null;
}
